import {
  client,
  type ListTariffsParams,
  type PaginatedTariffs,
  type Tariff,
} from './client';

const CACHE_PREFIX = 'musiccreator:tariffs:v1:';

type CachedTariffs = {
  tariffs: Tariff[];
  total: number;
  updatedAt: number;
};

const memoryCache = new Map<string, CachedTariffs>();
const inFlight = new Map<string, Promise<PaginatedTariffs>>();

function cacheKey(params: ListTariffsParams = {}): string {
  return `${CACHE_PREFIX}${JSON.stringify(params, Object.keys(params).sort())}`;
}

function readStorage(key: string): CachedTariffs | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as CachedTariffs;
    if (!Array.isArray(parsed.tariffs) || typeof parsed.updatedAt !== 'number') {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: CachedTariffs): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage can be unavailable/full; in-memory cache still works.
  }
}

export function getCachedTariffs(
  params: ListTariffsParams = {},
): CachedTariffs | null {
  const key = cacheKey(params);
  const memory = memoryCache.get(key);
  if (memory) return memory;

  const stored = readStorage(key);
  if (stored) memoryCache.set(key, stored);
  return stored;
}

export function refreshTariffs(
  params: ListTariffsParams = {},
): Promise<PaginatedTariffs> {
  const key = cacheKey(params);
  const existing = inFlight.get(key);
  if (existing) return existing;

  const request = client.tariffs
    .list(params)
    .then((response) => {
      const cached: CachedTariffs = {
        tariffs: response.tariffs,
        total: response.total,
        updatedAt: Date.now(),
      };

      memoryCache.set(key, cached);
      writeStorage(key, cached);
      return response;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, request);
  return request;
}

export type { CachedTariffs };
